// ============================================================
// TokenCard — the charted token, from the footer.
// ------------------------------------------------------------
// Clicking the footer status (which already shows the token symbol whenever
// activeData.status starts with "token:") slides this up from the bottom edge.
//
// Bottom sheet on BOTH desktop and phone, deliberately — it's anchored to the
// footer, and the footer is at the bottom on every viewport. A popover that
// flew out of the bottom-left corner on desktop and a sheet on phone would be
// two behaviours to maintain for one gesture.
//
// Data: Jupiter tokens/v2/search?query=<mint>. Same API family ScanPanel
// already uses, and the ONLY one that returns socials, audit flags, mcap and
// holder count in a single call. Module-level cache so re-opening the same
// token is instant and doesn't re-hit the API — you will open this on the same
// coin ten times in a session.
// ============================================================
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import TokenSocials from './TokenSocials'
import { getDexPaid, onDexStatus } from '../utils/dexStatus'
import { normalizeTokenImage } from '../utils/jupiter'

const SLIDE_MS = 260

// mint -> token object | null (null = fetched, not found — don't retry)
const cache = new Map()

async function fetchToken(mint) {
  if (cache.has(mint)) return cache.get(mint)
  try {
    const res = await fetch(`https://lite-api.jup.ag/tokens/v2/search?query=${mint}`)
    if (!res.ok) throw new Error(String(res.status))
    const json = await res.json()
    // search returns an ARRAY; take the exact mint match, not just [0] — a symbol
    // query can return imposters ahead of the real thing.
    const hit = Array.isArray(json)
      ? (json.find(t => t.id === mint) || json[0] || null)
      : null
    // This card fetches lite-api directly rather than going through
    // searchTokens, so it never inherited that function's icon normalization.
    // Heal before the value enters the module cache — the cache is the whole
    // point of this function, and an unnormalized URL cached here would keep
    // re-rendering broken for the rest of the session.
    const tok = hit ? { ...hit, icon: normalizeTokenImage(hit.icon) } : null
    cache.set(mint, tok)
    return tok
  } catch {
    cache.set(mint, null)
    return null
  }
}

const fmtMcap = (n) => {
  if (!(n > 0)) return null
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`
  return `$${Math.round(n)}`
}
const fmtNum = (n) => {
  if (!(n > 0)) return null
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`
  return String(Math.round(n))
}
// ORG = Jupiter's organicScore, the same value (and tiers) the scan cards chip.
// >=70 high, >=40 mid, else low — matches FootprintChartCanvas, so a token reads
// the same everywhere. NOT live: it only arrives from Jupiter's 30s REST poll and
// this card fetches once + caches, so ORG here is a snapshot from when you opened
// the card, not a ticker. Fine for a glanceable card.
const orgTier = (n) => (n >= 70 ? 'tier-high' : n >= 40 ? 'tier-mid' : 'tier-low')

const fmtAge = (iso) => {
  if (!iso) return null
  const ms = Date.now() - new Date(iso).getTime()
  if (!(ms > 0)) return null
  const h = ms / 3.6e6
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`
  if (h < 48) return `${Math.round(h)}h`
  return `${Math.round(h / 24)}d`
}

export default function TokenCard({ mint, onClose, boosted = false }) {
  const [tok, setTok] = useState(() => cache.get(mint) ?? undefined)  // undefined = loading
  const [copied, setCopied] = useState(false)
  // Mount closed, then open on the next frame — a CSS transition needs to SEE
  // the starting transform to animate from it. Mounting straight into the open
  // state means the browser has nothing to interpolate and it just appears.
  const [shown, setShown] = useState(false)
  // DexScreener paid status — resolves async off a shared, rate-limited
  // queue; the subscription just nudges a re-render when it lands.
  const [, setDexTick] = useState(0)
  useEffect(() => onDexStatus(() => setDexTick(t => t + 1)), [])
  const dex = getDexPaid(mint)
  const [leaving, setLeaving] = useState(false)
  const cardRef = useRef(null)
  const closeTimer = useRef(null)

  useEffect(() => {
    let dead = false
    if (cache.has(mint)) { setTok(cache.get(mint)); return }
    setTok(undefined)
    fetchToken(mint).then(t => { if (!dead) setTok(t) })
    return () => { dead = true }
  }, [mint])

  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true))
    return () => cancelAnimationFrame(id)
  }, [])

  // Close = slide back DOWN, then unmount. Unmounting on click would make the
  // card vanish, which reads as a bug next to a 260ms entrance.
  const close = () => {
    if (leaving) return
    setLeaving(true)
    setShown(false)
    closeTimer.current = setTimeout(onClose, SLIDE_MS)
  }
  useEffect(() => () => clearTimeout(closeTimer.current), [])

  useEffect(() => {
    const onDown = (e) => { if (!cardRef.current?.contains(e.target)) close() }
    const onKey = (e) => { if (e.key === 'Escape') close() }
    // Deferred a frame: the click that OPENED this is still finishing its
    // journey up the tree, and a listener attached synchronously catches it and
    // closes the card the instant it opens.
    const id = requestAnimationFrame(() => {
      document.addEventListener('pointerdown', onDown)
      window.addEventListener('keydown', onKey)
    })
    return () => {
      cancelAnimationFrame(id)
      document.removeEventListener('pointerdown', onDown)
      window.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaving])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(mint)
      setCopied(true)
      setTimeout(() => setCopied(false), 1400)
    } catch { /* clipboard blocked — the address is selectable anyway */ }
  }

  const loading = tok === undefined
  const name = tok?.symbol || tok?.name || `${mint.slice(0, 4)}…${mint.slice(-4)}`
  const stats = [
    ['MCAP', fmtMcap(tok?.mcap || tok?.fdv)],
    ['HOLDERS', fmtNum(tok?.holderCount)],
    ['LIQ', fmtMcap(tok?.liquidity)],
    ['AGE', fmtAge(tok?.firstPool?.createdAt)],
  ].filter(([, v]) => v)

  const ch24 = tok?.stats24h?.priceChange

  return createPortal(
    <>
      <style>{`
        .tc-sheet {
          position: fixed;
          left: 0; bottom: 0;          /* pinned to the LEFT edge, not centered.
                                          (right:0 + margin:auto was centering the
                                          max-width card.) */
          z-index: 70;                 /* footer popup rung — above panels, below modals */
          width: 100%;                 /* position:fixed + left:0 with NO width
                                          shrink-wraps to content — the previous
                                          comment claimed full-bleed on phone but
                                          nothing was making it true. This does. */
          max-width: 460px;            /* …and caps it into a left-anchored card
                                          on desktop. */
          box-sizing: border-box;      /* width:100% + the 1px borders must not
                                          overflow the viewport */
          background: var(--surface, #101018);
          border: 1px solid var(--seam, rgba(200,210,230,0.12));
          border-bottom: 0;
          border-radius: 10px 10px 0 0;
          box-shadow: 0 -12px 40px rgba(0,0,0,0.6);
          font-family: 'JetBrains Mono', monospace;
          transform: translateY(100%);
          transition: transform ${SLIDE_MS}ms cubic-bezier(0.32, 0.72, 0, 1);
        }
        .tc-sheet.shown { transform: translateY(0); }

        .tc-rail {
          display: flex; align-items: center; gap: 9px;
          padding: 10px 12px;
          border-bottom: 1px solid var(--seam, rgba(200,210,230,0.12));
        }
        .tc-logo {
          width: 26px; height: 26px; border-radius: 50%;
          flex-shrink: 0; object-fit: cover;
          background: var(--surface-rail, #14141d);
        }
        .tc-name {
          font-size: 13px; font-weight: 600; color: var(--text-hi, #c8cfdc);
          letter-spacing: 0.02em;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .tc-chg { font-size: 11px; font-variant-numeric: tabular-nums; }
        .tc-chg.up   { color: var(--up, #26d17f); }
        .tc-chg.down { color: var(--down, #ff4d6a); }
        /* Matches .jup-scan-organic from the scan cards: stacked value-over-label
           in a tinted, bordered box, tier-colored. Same component language so a
           token reads identically here and in the scan list. */
        .tc-org {
          display: inline-flex; flex-direction: column;
          align-items: center; justify-content: center;
          padding: 2px 6px; border-radius: 4px; corner-shape: bevel;
          border: 1px solid transparent;
          flex-shrink: 0; line-height: 1;
        }
        .tc-org.tier-high { background: rgba(70,212,31,0.06);  border-color: rgba(70,212,31,0.22); }
        .tc-org.tier-mid  { background: rgba(255,170,0,0.05);  border-color: rgba(255,170,0,0.22); }
        .tc-org.tier-low  { background: rgba(255,68,85,0.05);  border-color: rgba(255,68,85,0.20); }
        .tc-org-v {
          font-size: 12px; font-weight: 700; line-height: 1;
          letter-spacing: -0.02em; font-variant-numeric: tabular-nums;
        }
        .tc-org.tier-high .tc-org-v { color: #46d41f; }
        .tc-org.tier-mid  .tc-org-v { color: #ffaa00; }
        .tc-org.tier-low  .tc-org-v { color: #ff4455; }
        .tc-org-k {
          font-size: 7px; letter-spacing: 0.09em; margin-top: 1px;
          color: var(--text-lo, #5a6172); text-transform: uppercase;
        }
        .tc-attrs { display: inline-flex; align-items: center; gap: 5px; }
        .tc-dex   { background: rgba(0,209,255,0.06); border-color: rgba(0,209,255,0.24); }
        .tc-dex   .tc-org-v { color: #00d1ff; font-size: 11px; }
        .tc-boost { background: rgba(255,140,0,0.07); border-color: rgba(255,140,0,0.26); }
        .tc-boost .tc-org-v { font-size: 11px; }
        .tc-spacer { margin-left: auto; }
        .tc-x {
          background: none; border: 0; cursor: pointer; padding: 2px 4px;
          color: var(--text-lo, #5a6172); font-size: 14px; line-height: 1;
        }
        .tc-x:hover { color: var(--text-hi, #c8cfdc); }

        .tc-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(74px, 1fr));
          gap: 1px;
          background: var(--seam, rgba(200,210,230,0.12));
          border-bottom: 1px solid var(--seam, rgba(200,210,230,0.12));
        }
        .tc-stat { background: var(--surface, #101018); padding: 9px 10px; }
        .tc-stat-k {
          font-size: 8px; letter-spacing: 0.09em; text-transform: uppercase;
          color: var(--text-lo, #5a6172); margin-bottom: 3px;
        }
        .tc-stat-v {
          font-size: 12px; color: var(--text-hi, #c8cfdc);
          font-variant-numeric: tabular-nums;
        }

        .tc-addr {
          display: flex; align-items: center; gap: 8px;
          padding: 10px 12px;
        }
        .tc-mint {
          font-size: 9px; color: var(--text-mid, #8a93a6);
          word-break: break-all; user-select: all; cursor: text;
          line-height: 1.5;
        }
        .tc-copy {
          margin-left: auto; flex-shrink: 0;
          background: none; border: 1px solid var(--seam, rgba(200,210,230,0.12));
          border-radius: 4px; corner-shape: bevel;
          color: var(--text-mid, #8a93a6);
          font-family: inherit; font-size: 9px; letter-spacing: 0.05em;
          padding: 4px 8px; cursor: pointer;
          transition: color 0.12s, border-color 0.12s;
        }
        .tc-copy:hover { color: var(--text-hi, #c8cfdc); border-color: var(--text-lo, #5a6172); }
        .tc-copy.done { color: var(--connect-connected, #00ff88); border-color: var(--connect-connected, #00ff88); }

        .tc-links {
          display: flex; align-items: center; gap: 10px;
          padding: 9px 12px;
          border-top: 1px solid var(--seam, rgba(200,210,230,0.12));
        }
        .tc-links-none {
          font-size: 9px; color: var(--text-lo, #5a6172); letter-spacing: 0.04em;
        }
        .tc-safe { margin-left: auto; display: flex; gap: 5px; }
        .tc-flag {
          font-size: 8px; letter-spacing: 0.05em;
          padding: 2px 5px; border-radius: 3px; corner-shape: bevel;
          background: rgba(38,209,127,0.12); color: var(--up, #26d17f);
        }
        .tc-flag.bad { background: rgba(255,77,106,0.12); color: var(--down, #ff4d6a); }

        .tc-loading {
          padding: 22px 12px; text-align: center;
          font-size: 10px; color: var(--text-lo, #5a6172); letter-spacing: 0.08em;
        }
      `}</style>

      <div ref={cardRef} className={`tc-sheet${shown ? ' shown' : ''}`}>
        <div className="tc-rail">
          {tok?.icon
            ? <img className="tc-logo" src={tok.icon} alt="" onError={(e) => { e.target.style.visibility = 'hidden' }} />
            : <div className="tc-logo" />}
          <span className="tc-name">{name}</span>
          {typeof ch24 === 'number' && ch24 !== 0 && (
            <span className={`tc-chg ${ch24 >= 0 ? 'up' : 'down'}`}>
              {ch24 >= 0 ? '+' : ''}{ch24.toFixed(1)}%
            </span>
          )}
          {/* ORG chip — immediately after the %, per the ask. min-width keeps the
              label from reflowing while the score loads. */}
          {/* Attribute group: ORG · DEX · 🚀 — same chip language, one row.
              DEX = the team PAID DexScreener for a listing product; the
              rocket = active boosts. Different claims, different chips. */}
          <span className="tc-attrs">
            {typeof tok?.organicScore === 'number' && (
              <span className={`tc-org ${orgTier(tok.organicScore)}`}>
                <span className="tc-org-v">{Math.round(tok.organicScore)}</span>
                <span className="tc-org-k">ORG</span>
              </span>
            )}
            {dex?.paid && (
              <span className="tc-org tc-dex" title={`DexScreener paid: ${dex.types.join(', ')}`}>
                <span className="tc-org-v">✓</span>
                <span className="tc-org-k">DEX</span>
              </span>
            )}
            {boosted && (
              <span className="tc-org tc-boost" title="Active DexScreener boosts">
                <span className="tc-org-v">🚀</span>
                <span className="tc-org-k">BOOST</span>
              </span>
            )}
          </span>
          <span className="tc-spacer" />
          <button className="tc-x" onClick={close} aria-label="Close">✕</button>
        </div>

        {loading ? (
          <div className="tc-loading">LOADING…</div>
        ) : (
          <>
            {stats.length > 0 && (
              <div className="tc-stats">
                {stats.map(([k, v]) => (
                  <div className="tc-stat" key={k}>
                    <div className="tc-stat-k">{k}</div>
                    <div className="tc-stat-v">{v}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="tc-addr">
              <span className="tc-mint">{mint}</span>
              <button className={`tc-copy${copied ? ' done' : ''}`} onClick={copy}>
                {copied ? 'COPIED' : 'COPY'}
              </button>
            </div>

            <div className="tc-links">
              {/* Same component the scan cards use — one definition, one look. */}
              <TokenSocials token={tok} size={15} />
              {!(tok?.twitter || tok?.telegram || tok?.website) && (
                <span className="tc-links-none">NO LINKS</span>
              )}
              <span className="tc-safe">
                {tok?.audit?.mintAuthorityDisabled && <span className="tc-flag">MINT ✓</span>}
                {tok?.audit?.freezeAuthorityDisabled && <span className="tc-flag">FREEZE ✓</span>}
                {tok?.audit?.topHoldersPercentage > 30 && (
                  <span className="tc-flag bad">TOP10 {Math.round(tok.audit.topHoldersPercentage)}%</span>
                )}
              </span>
            </div>
          </>
        )}
      </div>
    </>,
    document.body
  )
}